// @ts-nocheck
import constants from "@store/constants";
import useCurrentBreakpointIndex from "@store/Framework/Common/Hooks/useCurrentBreakpointIndex";
import pxStringToNumberArray from "@store/Framework/Common/Utilities/pxStringToNumberArray";
import stringValueToNumberPx from "@store/Framework/Common/Utilities/stringValueToNumberPx";
import { HeaderStickyLevels, useHeaderContext } from "@store/Framework/Components/Header/HeaderContext/HeaderContext";
import { useScrollContext } from "@store/Framework/Components/ScrollContext/ScrollContext";
import { pxToRemArray, remToPxArray } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useMemo } from "react";
import { useTheme } from "styled-components";

export default () => {
    const theme = useTheme();
    const { scroll } = useScrollContext();
    const { headerLevel } = useHeaderContext();
    const currentBreakpointIndex = useCurrentBreakpointIndex();

    const isHeaderNormal = useMemo(() => headerLevel === HeaderStickyLevels.normal, [headerLevel]);
    const isHeaderSticky = useMemo(() => headerLevel === HeaderStickyLevels.sticky, [headerLevel]);
    const isHeaderStickyingSmall = useMemo(() => headerLevel === HeaderStickyLevels.stickyingSmall, [headerLevel]);
    const isHeaderStickySmall = useMemo(() => headerLevel === HeaderStickyLevels.stickySmall, [headerLevel]);

    const isHeaderStatic = useMemo(() => isHeaderNormal, [isHeaderNormal]);
    const isHeaderFixed = useMemo(
        () => isHeaderSticky || isHeaderStickyingSmall || isHeaderStickySmall,
        [isHeaderSticky, isHeaderStickyingSmall, isHeaderStickySmall],
    );

    const higherHeight = useMemo(
        () => stringValueToNumberPx(String(theme.design.header.height[currentBreakpointIndex]), "rem"),
        [theme.design.header.height, currentBreakpointIndex],
    );

    const lowerHeight = useMemo(
        () => stringValueToNumberPx(String(theme.design.header.height[0]), "rem"),
        [theme.design.header.height],
    );

    const higherPadding = useMemo(
        () => stringValueToNumberPx(String(theme.design.header.padding[currentBreakpointIndex]), "rem"),
        [theme.design.header.padding, currentBreakpointIndex],
    );

    const lowerPadding = useMemo(
        () => stringValueToNumberPx(String(theme.design.header.padding[0]), "rem"),
        [theme.design.header.padding],
    );

    const headerHeightGap = useMemo(() => higherHeight - lowerHeight, [higherHeight, lowerHeight]);

    const headerHeights = useMemo(() => {
        if (isHeaderNormal) {
            return theme.design.header.height;
        }

        if (isHeaderSticky) {
            return theme.design.header.height;
        }

        if (isHeaderStickyingSmall) {
            const stickyingSmallHeight = higherHeight + constants.secondaryHeaderHeight[0] - scroll;

            if (stickyingSmallHeight < lowerHeight) {
                return [theme.design.header.height[0]];
            }

            return pxToRemArray([stickyingSmallHeight]);
        }

        if (isHeaderStickySmall) {
            return [theme.design.header.height[0]];
        }

        return isHeaderStickySmall ? [theme.design.header.height[0]] : theme.design.header.height;
    }, [
        scroll,
        isHeaderNormal,
        isHeaderSticky,
        isHeaderStickyingSmall,
        isHeaderStickySmall,
        theme.design.header.height,
    ]);

    const headerHeightsNumber = useMemo(() => {
        const pxHeaderHeights = remToPxArray(headerHeights);
        return pxStringToNumberArray(pxHeaderHeights);
    }, [headerHeights]);

    const headerPaddings = useMemo(() => {
        if (isHeaderNormal) {
            return theme.design.header.padding;
        }

        if (isHeaderSticky) {
            return theme.design.header.padding;
        }

        if (isHeaderStickyingSmall) {
            const stickyingSmallPadding = higherPadding + constants.secondaryHeaderHeight[0] - scroll;

            if (stickyingSmallPadding < lowerPadding) {
                return [theme.design.header.padding[0]];
            }

            return pxToRemArray([stickyingSmallPadding]);
        }

        if (isHeaderStickySmall) {
            return [theme.design.header.padding[0]];
        }
    }, [
        scroll,
        isHeaderNormal,
        isHeaderSticky,
        isHeaderStickyingSmall,
        isHeaderStickySmall,
        theme.design.header.padding,
    ]);

    return {
        isHeaderNormal,
        isHeaderSticky,
        isHeaderStickyingSmall,
        isHeaderStickySmall,
        isHeaderFixed,
        isHeaderStatic,
        headerHeightGap,
        headerHeights,
        headerHeightsNumber,
        headerPaddings,
    };
};
